import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Search,
  Download,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Filter,
  X,
  Calendar,
  DollarSign,
  TrendingUp,
  Shield,
  FileText,
  Clock,
  AlertCircle,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvestmentPortfolioPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // State
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiSummary, setApiSummary] = useState({ total_invested: 0, total_outstanding: 0 });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('investment_date');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Drawer
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [loanDetails, setLoanDetails] = useState({
    repaymentSchedule: [],
    paymentHistory: [],
    documents: []
  });

  // Fetch investments
  const fetchInvestments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.pageSize.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/api/investments?${params}`, { headers });
      setInvestments(response.data.items);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        totalPages: response.data.total_pages
      }));
      // Use summary from API for accurate totals
      if (response.data.summary) {
        setApiSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Failed to fetch investments:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, sortBy, sortOrder, search, statusFilter, token]);

  useEffect(() => {
    fetchInvestments();
  }, [fetchInvestments]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch loan details
  const fetchLoanDetails = async (loanId) => {
    setDrawerLoading(true);
    try {
      const [historyRes, docsRes] = await Promise.all([
        axios.get(`${API_URL}/api/investments/${loanId}/payment-history`, { headers }),
        axios.get(`${API_URL}/api/investments/${loanId}/documents`, { headers })
      ]);

      setLoanDetails({
        repaymentSchedule: [],
        paymentHistory: historyRes.data,
        documents: docsRes.data
      });
    } catch (error) {
      console.error('Failed to fetch loan details:', error);
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleLoanClick = async (loan) => {
    setSelectedLoan(loan);
    setDrawerOpen(true);
    await fetchLoanDetails(loan.id);
  };

  // Export functions
  const handleExportCSV = () => {
    window.open(`${API_URL}/api/investments/export/csv?token=${token}`, '_blank');
  };

  // Format helpers
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'R 0.00';
    return `R ${new Intl.NumberFormat('en-ZA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-700 border-green-200',
      matured: 'bg-blue-100 text-blue-700 border-blue-200',
      arrears: 'bg-amber-100 text-amber-700 border-amber-200',
      default: 'bg-red-100 text-red-700 border-red-200',
      written_off: 'bg-gray-100 text-gray-700 border-gray-200'
    };
    const labels = {
      active: 'Active',
      matured: 'Matured',
      arrears: 'In Arrears',
      default: 'Default',
      written_off: 'Written Off'
    };
    return (
      <Badge className={`${styles[status] || styles.active} border`}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getPerformanceBadge = (classification) => {
    const styles = {
      performing: 'bg-green-50 text-green-700',
      sub_performing: 'bg-amber-50 text-amber-700',
      non_performing: 'bg-red-50 text-red-700',
      overdue: 'bg-red-100 text-red-800 font-semibold',
    };
    const labels = {
      performing: 'Performing',
      sub_performing: 'Sub-performing',
      non_performing: 'Non-performing',
      overdue: 'Overdue',
    };
    return (
      <span className={`text-xs px-2 py-0.5 rounded ${styles[classification] || styles.performing}`}>
        {labels[classification] || classification}
      </span>
    );
  };

  const getInterestTypeBadge = (type) => {
    const labels = {
      fixed: 'Fixed',
      variable: 'Variable',
      prime_linked: 'Prime-linked'
    };
    return labels[type] || type;
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getScheduleStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'overdue':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'partial':
        return <Clock className="h-4 w-4 text-amber-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  // Summary stats - use API summary for accurate totals across all pages
  const totalInvested = apiSummary.total_invested || investments.reduce((sum, inv) => sum + (inv.original_amount || 0), 0);
  const totalOutstanding = apiSummary.total_outstanding || investments.reduce((sum, inv) => sum + (inv.outstanding_balance || 0), 0);

  return (
    <div className="space-y-6" data-testid="investment-portfolio-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#212529] font-['Manrope']">Investment Portfolio</h1>
          <p className="text-[#6C757D] mt-1">Manage and track your loan participations</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            className="border-gray-200"
            data-testid="export-csv-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-gray-200"
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#1D2D2A]/10 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-[#1D2D2A]" />
              </div>
              <div>
                <p className="text-sm text-[#6C757D]">Total Invested</p>
                <p className="text-2xl font-bold text-[#212529]" data-testid="total-invested">
                  {formatCurrency(totalInvested)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#18A37C]/10 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-[#18A37C]" />
              </div>
              <div>
                <p className="text-sm text-[#6C757D]">Outstanding Balance</p>
                <p className="text-2xl font-bold text-[#212529]" data-testid="total-outstanding">
                  {formatCurrency(totalOutstanding)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#315C52]/10 flex items-center justify-center">
                <Shield className="h-6 w-6 text-[#315C52]" />
              </div>
              <div>
                <p className="text-sm text-[#6C757D]">Active Investments</p>
                <p className="text-2xl font-bold text-[#212529]" data-testid="active-count">
                  {pagination.total}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#ADB5BD]" />
              <Input
                placeholder="Search by loan reference or borrower..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 border-gray-200"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px] border-gray-200 relative z-10" data-testid="status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="z-50">
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="matured">Matured</SelectItem>
                <SelectItem value="arrears">In Arrears</SelectItem>
                <SelectItem value="default">Default</SelectItem>
              </SelectContent>
            </Select>
            {(search || statusFilter !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch('');
                  setStatusFilter('all');
                }}
                className="text-[#6C757D]"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Investments Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : investments.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-[#6C757D]">No investments found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead 
                      className="cursor-pointer hover:text-[#1D2D2A]"
                      onClick={() => handleSort('loan_reference')}
                    >
                      <div className="flex items-center gap-1">
                        Loan Reference
                        <ArrowUpDown className="h-3 w-3" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-right cursor-pointer hover:text-[#1D2D2A]"
                      onClick={() => handleSort('original_amount')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Original Amount
                        <ArrowUpDown className="h-3 w-3" />
                      </div>
                    </TableHead>
                    <TableHead className="text-right">Outstanding</TableHead>
                    <TableHead 
                      className="text-center cursor-pointer hover:text-[#1D2D2A]"
                      onClick={() => handleSort('interest_rate')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Rate
                        <ArrowUpDown className="h-3 w-3" />
                      </div>
                    </TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-center">Term (Tenor Remain)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Classification</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {investments.map((investment) => (
                    <TableRow 
                      key={investment.id}
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => handleLoanClick(investment)}
                      data-testid={`investment-row-${investment.id}`}
                    >
                      <TableCell className="font-medium text-[#1D2D2A]">
                        {investment.loan_reference}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(investment.original_amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(investment.outstanding_balance)}
                      </TableCell>
                      <TableCell className="text-center font-medium">
                        {typeof investment.interest_rate === 'number' ? investment.interest_rate.toFixed(2) : investment.interest_rate}%
                      </TableCell>
                      <TableCell className="text-sm text-[#6C757D]">
                        {getInterestTypeBadge(investment.interest_type)}
                      </TableCell>
                      <TableCell className={`text-center font-medium ${
                        (typeof investment.tenor_remain === 'number' && investment.tenor_remain < 0) ? 'text-red-600' : ''
                      }`}>
                        {investment.tenor_remain !== null && investment.tenor_remain !== undefined
                          ? `${investment.tenor_remain}`
                          : (investment.remaining_term_months ?? '-')}
                      </TableCell>
                      <TableCell>{getStatusBadge(investment.status)}</TableCell>
                      <TableCell>{getPerformanceBadge(investment.performance_classification)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
              <p className="text-sm text-[#6C757D]">
                Showing {((pagination.page - 1) * pagination.pageSize) + 1} to{' '}
                {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{' '}
                {pagination.total} investments
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-[#6C757D]">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.totalPages}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loan Detail Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="loan-detail-drawer">
          {selectedLoan && (
            <>
              <SheetHeader className="pb-4">
                <SheetTitle className="text-xl font-bold text-[#1D2D2A] font-['Manrope']">
                  {selectedLoan.loan_reference}
                </SheetTitle>
                <SheetDescription className="text-[#6C757D]">
                  {selectedLoan.borrower_name}
                </SheetDescription>
              </SheetHeader>

              <Tabs defaultValue="details" className="mt-4">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
                  <TabsTrigger value="payments" data-testid="tab-payments">Cash Flows</TabsTrigger>
                  <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
                </TabsList>

                <TabsContent value="details" className="space-y-4">
                  {/* Loan Details */}
                  <Card className="border border-gray-100">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base font-semibold">Loan Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-[#6C757D]">Borrower Sector</p>
                          <p className="font-medium capitalize">{selectedLoan.borrower_sector}</p>
                        </div>
                        <div>
                          <p className="text-[#6C757D]">Loan Purpose</p>
                          <p className="font-medium">{selectedLoan.loan_purpose}</p>
                        </div>
                        <div>
                          <p className="text-[#6C757D]">Investment Date</p>
                          <p className="font-medium">{formatDate(selectedLoan.investment_date)}</p>
                        </div>
                        <div>
                          <p className="text-[#6C757D]">Maturity Date</p>
                          <p className="font-medium">{formatDate(selectedLoan.maturity_date)}</p>
                        </div>
                        <div>
                          <p className="text-[#6C757D]">Original Amount</p>
                          <p className="font-medium">{formatCurrency(selectedLoan.original_amount)}</p>
                        </div>
                        <div>
                          <p className="text-[#6C757D]">Outstanding Balance</p>
                          <p className="font-medium">{formatCurrency(selectedLoan.outstanding_balance)}</p>
                        </div>
                        <div>
                          <p className="text-[#6C757D]">Interest Rate</p>
                          <p className="font-medium">{typeof selectedLoan.interest_rate === 'number' ? selectedLoan.interest_rate.toFixed(2) : selectedLoan.interest_rate}% p.a.</p>
                        </div>
                        <div>
                          <p className="text-[#6C757D]">Interest Type</p>
                          <p className="font-medium capitalize">{selectedLoan.interest_type?.replace('_', ' ')}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Status & Classification */}
                  <Card className="border border-gray-100">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base font-semibold">Status & Performance</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[#6C757D]">Current Status</span>
                        {getStatusBadge(selectedLoan.status)}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#6C757D]">Performance Classification</span>
                        {getPerformanceBadge(selectedLoan.performance_classification)}
                      </div>
                      {selectedLoan.performance_notes && (
                        <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                          <p className="text-sm font-medium text-amber-800">Performance Notes</p>
                          <p className="text-sm text-amber-700 mt-1">{selectedLoan.performance_notes}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Collateral */}
                  {selectedLoan.collateral_description && (
                    <Card className="border border-gray-100">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base font-semibold">Security / Collateral</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm">{selectedLoan.collateral_description}</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                <TabsContent value="payments">
                  {drawerLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map(i => <Skeleton key={i} className="h-12" />)}
                    </div>
                  ) : (
                    <ScrollArea className="h-[500px]">
                      <div className="space-y-3">
                        {(() => {
                          // Merge inflows and outflows.  A "flow" for a
                          // deal:
                          //   • Outflow  = negative amount (capital paid
                          //                out to acquire the loan, or
                          //                additional draw-downs)
                          //   • Inflow   = positive amount (interest,
                          //                capital repayments, buy-out
                          //                proceeds)
                          // We split by sign so the UI can colour each.
                          const flows = (loanDetails.paymentHistory || []).map(p => {
                            // payment-history endpoint sends `amount` as
                            // absolute positive. We infer direction from
                            // distribution_type. Any type that isn't
                            // clearly an inflow is treated as outflow.
                            const INFLOW_TYPES = new Set([
                              'interest', 'capital', 'repayment',
                              'deal_repayment', 'principal_return',
                              'payment', 'distribution'
                            ]);
                            const dt = (p.distribution_type || '').toLowerCase();
                            const isInflow = INFLOW_TYPES.has(dt);
                            return {
                              ...p,
                              signed_amount: isInflow ? Math.abs(p.amount) : -Math.abs(p.amount),
                              is_inflow: isInflow,
                            };
                          });
                          flows.sort((a, b) => (a.distribution_date < b.distribution_date ? 1 : -1));
                          const totalIn = flows.filter(f => f.is_inflow).reduce((s, f) => s + Math.abs(f.signed_amount), 0);
                          const totalOut = flows.filter(f => !f.is_inflow).reduce((s, f) => s + Math.abs(f.signed_amount), 0);
                          const netFlow = totalIn - totalOut;
                          return (
                            <>
                              <div className="grid grid-cols-3 gap-2 pb-3" data-testid="cashflow-summary">
                                <Card className="border-emerald-200 bg-emerald-50">
                                  <CardContent className="py-2 px-3">
                                    <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-medium">Inflows</p>
                                    <p className="text-sm font-mono font-semibold text-emerald-800" data-testid="cashflow-inflows">
                                      {formatCurrency(totalIn)}
                                    </p>
                                  </CardContent>
                                </Card>
                                <Card className="border-red-200 bg-red-50">
                                  <CardContent className="py-2 px-3">
                                    <p className="text-[10px] uppercase tracking-wide text-red-700 font-medium">Outflows</p>
                                    <p className="text-sm font-mono font-semibold text-red-800" data-testid="cashflow-outflows">
                                      {formatCurrency(totalOut)}
                                    </p>
                                  </CardContent>
                                </Card>
                                <Card className={`${netFlow >= 0 ? 'border-slate-200 bg-slate-50' : 'border-amber-200 bg-amber-50'}`}>
                                  <CardContent className="py-2 px-3">
                                    <p className="text-[10px] uppercase tracking-wide text-slate-700 font-medium">Net</p>
                                    <p className={`text-sm font-mono font-semibold ${netFlow >= 0 ? 'text-slate-800' : 'text-amber-800'}`} data-testid="cashflow-net">
                                      {netFlow >= 0 ? '+' : ''}{formatCurrency(netFlow)}
                                    </p>
                                  </CardContent>
                                </Card>
                              </div>
                              {flows.length === 0 && (
                                <p className="text-center text-[#6C757D] py-8">No cash flows recorded for this deal</p>
                              )}
                              {flows.map((flow) => (
                                <Card
                                  key={flow.id || `${flow.distribution_date}-${flow.signed_amount}`}
                                  className={`border ${flow.is_inflow ? 'border-emerald-100' : 'border-red-100'}`}
                                  data-testid={`cashflow-row-${flow.id || flow.distribution_date}`}
                                >
                                  <CardContent className="py-3">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className={`font-medium font-mono ${flow.is_inflow ? 'text-emerald-700' : 'text-red-700'}`}>
                                          {flow.is_inflow ? '+' : '−'}{formatCurrency(Math.abs(flow.signed_amount))}
                                        </p>
                                        <p className="text-xs text-[#6C757D] mt-0.5 capitalize">
                                          {flow.distribution_type?.replace(/_/g, ' ') || (flow.is_inflow ? 'Inflow' : 'Outflow')}
                                        </p>
                                      </div>
                                      <div className="text-right">
                                        <p className="text-sm">{formatDate(flow.distribution_date)}</p>
                                        <Badge variant="outline" className="text-xs mt-1">
                                          {flow.status}
                                        </Badge>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </>
                          );
                        })()}
                      </div>
                    </ScrollArea>
                  )}
                </TabsContent>

                <TabsContent value="documents">
                  {drawerLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map(i => <Skeleton key={i} className="h-12" />)}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {loanDetails.documents.map((doc) => (
                        <Card key={doc.id} className="border border-gray-100 cursor-pointer hover:border-[#18A37C]/50 transition-colors">
                          <CardContent className="py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded bg-[#1D2D2A]/10 flex items-center justify-center">
                                <FileText className="h-5 w-5 text-[#1D2D2A]" />
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-sm">{doc.document_name}</p>
                                <p className="text-xs text-[#6C757D] capitalize">
                                  {doc.document_type.replace('_', ' ')} • {formatDate(doc.uploaded_at)}
                                </p>
                              </div>
                              <Download className="h-4 w-4 text-[#6C757D]" />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      {loanDetails.documents.length === 0 && (
                        <p className="text-center text-[#6C757D] py-8">No documents available</p>
                      )}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default InvestmentPortfolioPage;
